<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="760" scrollable>
    <v-card class="create-plan-chooser" :style="{ '--chooser-accent': accentColor }">
      <v-card-title class="pb-0">
        <span class="text-h6">{{ title || $t('components.CreatePlanChooser.title') }}</span>
        <v-spacer></v-spacer>
        <v-btn icon small :aria-label="$t('components.CreatePlanChooser.close')" @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <template v-if="periodOption">
        <div class="px-6 pt-1 pb-4 body-2 grey--text text--darken-1">{{ $t('components.CreatePlanChooser.choosePeriod', { option: periodOption.title }) }}</div>
        <v-card-text class="pb-2">
          <div class="d-flex flex-wrap justify-center period-pickers">
            <div class="mb-4">
              <div class="overline grey--text text--darken-1 mb-1">{{ $t(isMonthPeriod ? 'components.CreatePlanChooser.startMonth' : 'components.CreatePlanChooser.startDate') }}</div>
              <v-date-picker
                v-model="periodStart"
                no-title
                flat
                :type="periodOption.period.type"
                :min="periodOption.period.min"
                :max="startMax"
                :picker-date.sync="startPickerDate"
                color="primary"
                class="period-picker"
              ></v-date-picker>
            </div>
            <div class="mb-4">
              <div class="overline grey--text text--darken-1 mb-1">{{ $t(isMonthPeriod ? 'components.CreatePlanChooser.endMonth' : 'components.CreatePlanChooser.endDate') }}</div>
              <v-date-picker
                v-model="periodEnd"
                no-title
                flat
                :type="periodOption.period.type"
                :min="endMin"
                :max="periodOption.period.max"
                :picker-date.sync="endPickerDate"
                color="primary"
                class="period-picker"
              ></v-date-picker>
            </div>
          </div>
          <div class="period-summary">
            <div class="overline grey--text text--darken-1">{{ $t('components.CreatePlanChooser.selectedPeriod') }}</div>
            <div class="subtitle-1 font-weight-bold mb-2">{{ periodLabel }}</div>
            <div v-for="(row, index) in periodSummaryRows" :key="index" class="d-flex align-center py-1" :class="{ 'period-summary__total mt-2 pt-3': row.total }">
              <v-icon small :color="row.color || 'grey'" class="mr-2">{{ row.icon }}</v-icon>
              <span class="body-2" :class="{ 'font-weight-bold': row.total }">{{ row.label }}</span>
              <v-spacer></v-spacer>
              <span class="body-2" :class="{ 'font-weight-bold': row.total }">{{ row.value }}</span>
            </div>
            <div v-if="periodOption.period.hint" class="caption grey--text mt-3">{{ periodOption.period.hint }}</div>
          </div>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-btn text @click="periodOption = null">
            <v-icon left>mdi-arrow-left</v-icon>{{ $t('components.CreatePlanChooser.back') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" depressed :disabled="!periodValid" @click="confirmPeriod">
            {{ $t('components.CreatePlanChooser.continue') }}<v-icon right>mdi-arrow-right</v-icon>
          </v-btn>
        </v-card-actions>
      </template>
      <div v-if="!periodOption" class="px-6 pt-1 pb-1 body-2 grey--text text--darken-1">{{ subtitle || $t('components.CreatePlanChooser.subtitle') }}</div>
      <v-card-text v-if="!periodOption" class="pt-3 pb-6">
        <div
          v-for="option in featuredOptions"
          :key="option.id"
          class="option-card option-card--featured d-flex align-center"
          :class="{ 'option-card--disabled': option.disabled }"
          :tabindex="option.disabled ? -1 : 0"
          role="button"
          :aria-disabled="option.disabled ? 'true' : 'false'"
          @click="choose(option)"
          @keydown.enter.prevent="choose(option)"
          @keydown.space.prevent="choose(option)"
        >
          <div class="option-icon option-icon--large mr-4">
            <v-icon color="primary" large>{{ option.icon }}</v-icon>
          </div>
          <div class="flex-grow-1">
            <div class="subtitle-1 font-weight-bold">{{ option.title }}</div>
            <div class="body-2 grey--text text--darken-1">{{ option.description }}</div>
          </div>
          <v-icon class="option-chevron ml-2">mdi-chevron-right</v-icon>
        </div>

        <template v-if="templateOptions.length > 0 || loading">
          <div class="overline grey--text text--darken-1 mt-6 mb-2">{{ $t('components.CreatePlanChooser.templates') }}</div>
          <div v-if="loading && templateOptions.length === 0" class="py-6 text-center">
            <v-progress-circular indeterminate color="primary" size="28" width="3"></v-progress-circular>
            <div class="body-2 grey--text mt-3">{{ $t('components.CreatePlanChooser.loadingTemplates') }}</div>
          </div>
          <v-row v-else dense>
            <v-col v-for="option in templateOptions" :key="option.id" cols="12" sm="6" class="d-flex">
              <div
                class="option-card d-flex flex-column flex-grow-1"
                :class="{ 'option-card--disabled': option.disabled }"
                :tabindex="option.disabled ? -1 : 0"
                role="button"
                :aria-disabled="option.disabled ? 'true' : 'false'"
                @click="choose(option)"
                @keydown.enter.prevent="choose(option)"
                @keydown.space.prevent="choose(option)"
              >
                <div class="d-flex align-center mb-3">
                  <div class="option-icon">
                    <v-icon color="primary">{{ option.icon }}</v-icon>
                  </div>
                  <v-spacer></v-spacer>
                  <v-chip v-if="option.tag" x-small outlined color="primary" class="option-tag">{{ option.tag }}</v-chip>
                </div>
                <div class="subtitle-2 font-weight-bold mb-1">{{ option.title }}</div>
                <div class="body-2 grey--text text--darken-1">{{ option.description }}</div>
                <div v-if="option.disabled && option.disabledReason" class="caption grey--text mt-2 d-flex align-center">
                  <v-icon x-small color="grey" class="mr-1">mdi-information-outline</v-icon>
                  {{ option.disabledReason }}
                </div>
              </div>
            </v-col>
          </v-row>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment'

// Modal launcher for the "Create" button of the Plan Optimizer, Plan Calculator and Budget Simulator.
// Each option is { id, icon, title, description, tag, featured, disabled, disabledReason, period };
// featured options render as a wide card on top, the rest as a template grid.
//
// An option with a `period` asks for a start and an end inside the modal before it is chosen:
// period = { type: 'month' | 'date', min, max, default: [from, to], view, hint, summary (range) -> rows },
// in the picker's format (YYYY-MM or YYYY-MM-DD); default (optional) comes selected, and without
// it both calendars open on view. summary returns the rows shown under the calendars,
// each { icon, color, label, value, total }. The option is then emitted as select(id, { from, to }).
export default {
  data () {
    return {
      periodOption: null,
      periodStart: null,
      periodEnd: null,
      startPickerDate: null,
      endPickerDate: null
    }
  },
  props: {
    // Header texts; default to the generic "Create a plan" ones.
    title: {
      type: String,
      default: ''
    },
    subtitle: {
      type: String,
      default: ''
    },
    value: {
      type: Boolean,
      default: false
    },
    options: {
      type: Array,
      default: function () { return [] }
    },
    // True while the model report (source of the templates) is still being fetched.
    loading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    featuredOptions () {
      return this.options.filter(function (option) { return option.featured })
    },
    templateOptions () {
      return this.options.filter(function (option) { return !option.featured })
    },
    accentColor () {
      return this.$vuetify.theme.currentTheme.primary
    },
    isMonthPeriod () {
      return this.periodOption.period.type === 'month'
    },
    // A monthly period can be a single month; a weekly one needs its end after its start.
    startMax () {
      if (!this.periodEnd) return this.periodOption.period.max
      return this.isMonthPeriod ? this.periodEnd : moment(this.periodEnd).subtract(1, 'day').format('YYYY-MM-DD')
    },
    endMin () {
      if (!this.periodStart) return this.periodOption.period.min
      return this.isMonthPeriod ? this.periodStart : moment(this.periodStart).add(1, 'day').format('YYYY-MM-DD')
    },
    periodValid () {
      if (!this.periodStart || !this.periodEnd) return false
      return this.isMonthPeriod ? this.periodStart <= this.periodEnd : this.periodStart < this.periodEnd
    },
    periodLabel () {
      const format = this.isMonthPeriod ? 'MMM YYYY' : 'll'
      const start = this.periodStart ? moment(this.periodStart).format(format) : this.$t('components.CreatePlanChooser.pickStart')
      const end = this.periodEnd ? moment(this.periodEnd).format(format) : this.$t('components.CreatePlanChooser.pickEnd')
      return `${start} → ${end}`
    },
    periodSummaryRows () {
      if (!this.periodValid || !this.periodOption.period.summary) return []
      return this.periodOption.period.summary({ from: this.periodStart, to: this.periodEnd })
    }
  },
  watch: {
    // Every opening starts on the option list.
    value (open) {
      if (open) this.periodOption = null
    },
    // With no end picked yet, the end calendar follows the start so the end is picked nearby.
    periodStart (start) {
      if (start && !this.periodEnd) {
        this.endPickerDate = this.pickerView(start)
      }
    }
  },
  methods: {
    choose: function (option) {
      if (option.disabled) return
      if (option.period) {
        const range = option.period.default || []
        this.periodStart = range.length > 0 ? range[0] : null
        this.periodEnd = range.length > 1 ? range[1] : null
        this.startPickerDate = this.pickerView(this.periodStart || option.period.view, option)
        this.endPickerDate = this.pickerView(this.periodEnd || option.period.view, option)
        this.periodOption = option
        return
      }
      this.$emit('input', false)
      this.$emit('select', option.id)
    },
    // The page a calendar shows for date: its year for month calendars, its month for day ones.
    pickerView: function (date, option) {
      if (!date) return null
      const type = (option || this.periodOption).period.type
      return type === 'month' ? date.slice(0, 4) : date.slice(0, 7)
    },
    confirmPeriod: function () {
      if (!this.periodValid) return
      const id = this.periodOption.id
      const period = { from: this.periodStart, to: this.periodEnd }
      this.$emit('input', false)
      this.$emit('select', id, period)
    }
  }
}
</script>

<style scoped>
.option-card {
  position: relative;
  padding: 16px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.option-card--featured {
  padding: 18px 20px;
}

.option-card:not(.option-card--disabled):hover,
.option-card:not(.option-card--disabled):focus-visible {
  border-color: var(--chooser-accent);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.option-card--disabled {
  cursor: default;
  opacity: 0.55;
}

.option-icon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
}

.option-icon--large {
  width: 52px;
  height: 52px;
}

/* Tinted circle in the brand's primary color, which varies by hostname. */
.option-icon::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: var(--chooser-accent);
  opacity: 0.14;
}

.option-chevron {
  transition: transform 0.15s ease;
}

.option-card:hover .option-chevron {
  transform: translateX(3px);
}

.option-tag {
  font-weight: 600;
}

.period-picker {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px;
}

.period-pickers {
  gap: 24px;
}

.period-summary {
  max-width: 604px;
  margin: 0 auto;
}

.period-summary__total {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
